/* istanbul ignore file */
import { useDispatch } from 'react-redux';
import { OccupancyType } from '@common/entities';
import { queryClient } from 'AppWrapper';
import { updateStudentUnitSelection } from 'state/slices/studentUnitSelectionSlice';
import { updateUnitSelection, updateWaitlistSelections } from 'state/slices/unitSelectionSlice';

import { useSaveResponse } from 'hooks/useSaveResponse';
import { useLocale } from 'hooks/utils/useLocale';
import { getAvailableUnitsAndLeaseTermStudent, getFloorPlans } from 'services';
import { addToWaitlist as addToWaitlistService } from 'services/applicant';
import type { FloorPlan } from 'types/floorplan-page-types';
import { getTodaysDate } from 'utils/date';
import {
  extractUnitSelectionFromLinkAndSession,
  extractWaitlistSelectionFromLinkAndSession,
  type LaunchSelectionContext,
  readLaunchSelectionContextFromSession,
  shouldTreatLaunchAsWaitlist,
} from 'utils/floorPlanLaunchSelection';
import handleError from 'utils/handleError';
import { clearAppliedFloorPlanSelectionFromSession } from 'utils/viewerLaunchContextSession';

import useGetAvailableUnitAndLeaseTerm from './useGetAvailableUnitsAndLeaseTerms';

export function resolveLaunchLeaseTermId(
  launchLeaseTermId: number | undefined,
  fetchedLeaseTermId: number | undefined,
): number | undefined {
  return launchLeaseTermId ?? fetchedLeaseTermId;
}

export function buildStudentLaunchSelection(
  leaseTermId: number | undefined,
  leaseStartWindowId: number | undefined,
  spaceConfigurationId: number | undefined,
) {
  if (leaseTermId == null && spaceConfigurationId == null) return undefined;

  return {
    ...(leaseTermId != null && {
      selectedLeaseTermId: String(leaseTermId),
      selectedLeaseStartWindowId:
        leaseStartWindowId != null ? String(leaseStartWindowId) : undefined,
    }),
    ...(spaceConfigurationId != null && {
      availableSpaceConfigurationIds: [spaceConfigurationId],
    }),
  };
}

export function isStudentOccupancy(occupancyType: string | undefined): boolean {
  return occupancyType === OccupancyType.Student;
}

export function buildLaunchFloorPlanStudentParams(
  isStudentLaunch: boolean,
  leaseTermId: number | undefined,
  leaseStartWindowId: number | undefined,
  spaceConfigurationId: number | undefined,
): Parameters<typeof getFloorPlans>[3] {
  if (!isStudentLaunch) {
    return undefined;
  }

  return {
    ...(leaseTermId != null && { leaseTermIds: [leaseTermId] }),
    ...(leaseStartWindowId != null && { leaseStartWindowIds: [leaseStartWindowId] }),
    ...(spaceConfigurationId != null && { spaceConfigurationIds: [spaceConfigurationId] }),
  };
}

export function useSetFloorPlanPageDataFromLink() {
  const dispatch = useDispatch();
  const locale = useLocale();

  const saveResponse = useSaveResponse();
  const { fetchAvailableUnitsAndLeaseTerm } = useGetAvailableUnitAndLeaseTerm(locale);

  async function updateResponse(
    currentPageId: string,
    applicationId: string,
    propertyId: number,
    data: { [key: string]: any },
    responseId?: string,
  ) {
    try {
      await saveResponse(
        {
          currentPageId,
          applicationId,
          propertyId,
          responses: [data],
        },
        responseId,
      );
    } catch {
      throw new Error('Error while saving floorplan details.');
    }
  }

  async function fetchFloorPlans(
    propertyId: number,
    floorPlanId: number,
    moveInDate: string,
    studentParams?: Parameters<typeof getFloorPlans>[3],
  ) {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['floor-plans', propertyId, moveInDate, studentParams, locale],
        queryFn: () => getFloorPlans(propertyId, moveInDate, undefined, studentParams),
      });

      const selectedFloorPlan = data.find((i: FloorPlan) => i.id === floorPlanId);
      if (selectedFloorPlan || studentParams == null) return selectedFloorPlan;

      // A launch-selected floorplan remains authoritative even if Core Config
      // cannot apply the optional student filters without a lease term. Retry
      // without those filters before treating the selection as unavailable.
      const unfilteredData = await queryClient.fetchQuery({
        queryKey: ['floor-plans', propertyId, moveInDate, 'launch-fallback', locale],
        queryFn: () => getFloorPlans(propertyId, moveInDate),
      });
      return unfilteredData.find((i: FloorPlan) => i.id === floorPlanId);
    } catch {
      throw new Error('Error while fetching floor plans.');
    }
  }

  async function initialize(
    url: string,
    propertyId: number,
    applicationId: string,
    pageId: string,
    responseId?: string,
    occupancyType?: string,
  ) {
    const ctx = readLaunchSelectionContextFromSession();
    const hadLaunchSelection = ctx != null;
    let result: number;
    if (shouldTreatLaunchAsWaitlist(url, ctx)) {
      result = await initializeWaitlistSelection(
        url,
        propertyId,
        applicationId,
        pageId,
        responseId,
        ctx,
      );
    } else {
      result = await initializeUnitSelection(
        url,
        propertyId,
        applicationId,
        pageId,
        responseId,
        ctx,
        occupancyType,
      );
    }

    if (hadLaunchSelection && result === 0) {
      clearAppliedFloorPlanSelectionFromSession();
    }

    return result;
  }

  async function initializeWaitlistSelection(
    url: string,
    propertyId: number,
    applicationId: string,
    pageId: string,
    responseId: string | undefined,
    ctx: LaunchSelectionContext | null,
  ) {
    const { waitlistFloorplanId } = extractWaitlistSelectionFromLinkAndSession(url, ctx);

    if (!waitlistFloorplanId) throw new Error('Missing floorplan id for waitlist.');
    try {
      await addToWaitlistService(waitlistFloorplanId);
      const waitlistSelections = {
        waitlistSelections: { floorplanIds: [waitlistFloorplanId] },
      };
      await updateResponse(pageId, applicationId, propertyId, waitlistSelections, responseId);
      dispatch(updateWaitlistSelections(waitlistSelections));
      return 0;
    } catch (err: any) {
      handleError(`Form Viewer - Error updating waitlist selection: ${err}`, `${err.message}`);

      return 1;
    }
  }

  async function initializeUnitSelection(
    url: string,
    propertyId: number,
    applicationId: string,
    pageId: string,
    responseId: string | undefined,
    ctx: LaunchSelectionContext | null,
    occupancyType: string | undefined,
  ) {
    const todaysDate = getTodaysDate();

    try {
      const {
        moveInDate: initialMoveInDate,
        floorplanId: floorPlanId,
        unitSpaceId,
        termMonth,
        leaseTermId: ctxLeaseTermId,
        spaceConfigurationId,
        leaseStartWindowId,
      } = extractUnitSelectionFromLinkAndSession(url, ctx);

      // The resolved form is the source of truth for occupancy. Lease-term and
      // start-window IDs are also valid for conventional properties, so they
      // must never be used to infer the student flow.
      const isStudentLaunch = isStudentOccupancy(occupancyType);
      const shouldDeferFloorPlanSelection = isStudentLaunch && spaceConfigurationId == null;

      // Student Floor Plans uses this slice as the source of truth for the selected
      // lease term. Seed it only for the unit-selection route; waitlist launches
      // intentionally do not establish a student unit or lease-term selection.
      const studentLaunchSelection =
        isStudentLaunch || spaceConfigurationId != null
          ? buildStudentLaunchSelection(ctxLeaseTermId, leaseStartWindowId, spaceConfigurationId)
          : undefined;
      if (studentLaunchSelection) {
        dispatch(updateStudentUnitSelection(studentLaunchSelection));
      }

      if (!floorPlanId) return 1;

      let moveInDate = initialMoveInDate;
      if (!moveInDate) moveInDate = todaysDate;

      const studentFloorPlanParams = buildLaunchFloorPlanStudentParams(
        isStudentLaunch,
        ctxLeaseTermId,
        leaseStartWindowId,
        spaceConfigurationId,
      );

      let floorPlan: any;
      try {
        floorPlan = await fetchFloorPlans(
          propertyId,
          floorPlanId,
          moveInDate,
          studentFloorPlanParams,
        );
      } catch {
        // Floor plan API may be unavailable; fall through to save launch context data directly
      }

      let unit: any;
      let leaseTerm: any;
      if (floorPlan && unitSpaceId) {
        try {
          if (isStudentLaunch) {
            const units = await getAvailableUnitsAndLeaseTermStudent(
              propertyId,
              floorPlanId,
              moveInDate,
              undefined,
              false,
              spaceConfigurationId != null ? [spaceConfigurationId] : [],
              ctxLeaseTermId,
              leaseStartWindowId,
            );
            unit = units.find((candidate) =>
              candidate.unitSpaces?.some(
                (unitSpace) => String(unitSpace.unitSpaceId) === String(unitSpaceId),
              ),
            );
            if (!unit) throw new Error('Unit is not available.');
            if (ctxLeaseTermId != null) {
              leaseTerm = {
                id: ctxLeaseTermId,
                name: '',
                amount: 0,
                rateFrequency: 'monthly',
                termMonth,
                deposit: 0,
                leaseStartWindowId: leaseStartWindowId != null ? String(leaseStartWindowId) : '',
              };
            }
          } else {
            const result = await fetchAvailableUnitsAndLeaseTerm(
              propertyId,
              floorPlanId,
              moveInDate,
              unitSpaceId,
              termMonth,
              undefined,
              ctxLeaseTermId,
            );
            unit = result.unit;
            leaseTerm = result.leaseTerm;
          }
        } catch {
          // Available-units API may be unavailable; continue with launch context data
        }
      }

      // The generic unit lookup can resolve the property's default term. A launch
      // context represents an explicit Prospect Portal choice and must survive the
      // post-sign-up response reload, so persist it ahead of the lookup result.
      const resolvedLeaseTermId = resolveLaunchLeaseTermId(ctxLeaseTermId, leaseTerm?.id);
      const resolvedTermMonth = leaseTerm?.termMonth ?? termMonth;
      // Fall back to context unitSpaceId when fetch did not resolve a unit object.
      let unitFields: { unitId?: number; unitSpaceId?: number } = {};
      if (unit) {
        unitFields = {
          unitId: unit.id,
          unitSpaceId: isStudentLaunch ? unitSpaceId : unit.unitSpaceId,
        };
      } else if (unitSpaceId) {
        unitFields = { unitSpaceId };
      }

      await updateResponse(
        pageId,
        applicationId,
        propertyId,
        {
          floorPlanData: {
            floorplanId: floorPlan?.id ?? floorPlanId,
            ...unitFields,
            ...(resolvedLeaseTermId != null && { leaseTermId: resolvedLeaseTermId }),
            ...(resolvedTermMonth ? { termMonth: resolvedTermMonth } : {}),
            ...(spaceConfigurationId != null && { spaceConfigurationId }),
            ...(leaseStartWindowId != null && { leaseStartWindowId }),
            moveInDate,
          },
        },
        responseId,
      );

      // A student bulk-assignment launch without a space configuration is not a
      // complete selection. Keep the requested lease term, but let the applicant
      // choose a floor plan and space configuration on the Floor Plans page.
      if (floorPlan && !shouldDeferFloorPlanSelection) {
        dispatch(
          updateUnitSelection({
            floorPlan,
            ...(unit && { unit }),
            ...(leaseTerm && { leaseTerm }),
            moveInDate,
          }),
        );
      }

      if (isStudentLaunch && unit && spaceConfigurationId != null) {
        dispatch(
          updateStudentUnitSelection({
            selectedSpace: {
              unitSpaceId: unitSpaceId.toString(),
              spaceConfigurationId: String(spaceConfigurationId),
            },
          }),
        );
      }

      return 0;
    } catch (err: any) {
      handleError(`Form Viewer - Error updating unit selection: ${err}`, `${err.message}`);

      return 1;
    }
  }

  return initialize;
}
